/**
 * (5) ViewModel 整形 (package 内部・非公開)。
 *
 * (3) `GlobalNaviDataV2` × 静的メニュー定義 (`config/qantProductList`) を突き合わせて、
 * `<GlobalNavi>` が描画に使う形に畳む。data-model.md 参照。
 */

import type { GlobalNaviDataV2, Platform } from '../contract/index.js';
import { filterProducts } from '../filter/filterProducts.js';
import type { ProductMenu } from '../types/menuModel.js';

export type NormalizedNaviView = {
  /** static config 由来の ProductMenu のうち、current.products で accessible 判定済みのもの */
  products: readonly ProductMenu[];
  /** 表示用ユーザ identity */
  user: { name: string; avatarUrl?: string };
  /** 表示用 organization / project (project トリガに利用)。current 未確定時は無い */
  organization?: { name: string };
  project?: { name: string };
  /** どちらの基盤を自基盤として描画するか (href 解決等に使う) */
  selfPlatform: Platform;
};

export type NormalizeParams = {
  data: GlobalNaviDataV2;
  /**
   * 自基盤の静的メニュー定義。consumer が独自定義を渡せるが、
   * 通常は default の `qantProductList` を使う。
   */
  staticProducts: readonly ProductMenu[];
};

/**
 * `GlobalNaviDataV2.current?.products`（両基盤統合済み）から accessible な product key 集合を導出し、
 * static config に `filterProducts` を適用する。
 *
 * 同一 product key（例: voc）が Karte / QANT 両方に載る場合は、いずれかで accessible なら表示する。
 * `current` が無ければプロダクト 0 件（プロジェクト切替 Popover は呼び出し側で別途描画する）。
 */
export const normalize = (params: NormalizeParams): NormalizedNaviView => {
  const { data, staticProducts } = params;
  const currentProducts = data.current?.products ?? [];

  const accessibleProductNames = new Set<string>(currentProducts.filter((p) => p.accessible).map((p) => p.key));

  // KARTE 本体 (連携プロダクト): karte 基盤のデータが取得できている場合のみ表示する。
  // projectIds.karte は移行先 mapping からも補完される。organizationIds.karte は KARTE current 取得時だけ載る。
  if (data.current?.organizationIds.karte !== undefined) {
    accessibleProductNames.add('karte');
  }

  // facts が載っている platform の述語でメニューを出し分ける。
  // 無ければ filterProducts が condition 評価をスキップし、product 単位の表示にフォールバックする。
  const products = filterProducts(staticProducts, { accessibleProductNames, facts: data.current?.facts });

  return {
    products,
    user: {
      name: data.user.name,
      ...(data.user.avatarUrl ? { avatarUrl: data.user.avatarUrl } : {}),
    },
    ...(data.current
      ? {
          organization: { name: data.current.organization.name },
          project: { name: data.current.project.name },
        }
      : {}),
    selfPlatform: data.selfPlatform,
  };
};
